import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { message } from "antd";
import rsbgLogo from "../data/rsbgloballogo.jpg";
import { resetPassword } from "apicalls/usersApiCall";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    return minLength && hasUpperCase && hasLowerCase && hasNumber;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    // Validation
    if (!newPassword || !confirmPassword) {
      message.error("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      message.error("Passwords do not match");
      return;
    }

    if (!validatePassword(newPassword)) {
      message.error("Password must be at least 8 characters with uppercase, lowercase, and number");
      return;
    }

    if (!token) {
      message.error("Invalid or missing reset token");
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        forgot_pwd_token: token,
        password: newPassword
      }
      const response = await resetPassword(payload);
      if (response?.success) {
        message.success(response?.message);
        navigate("/login", { replace: true });
      } else {
        message.error(response?.message);
        return;
      }
      
      
    } catch (error) {
      console.error("Reset password error:", error);
      message.error(error.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: "", color: "" };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { strength: 33, text: "Weak", color: "bg-red-500" };
    if (strength <= 3) return { strength: 66, text: "Medium", color: "bg-yellow-500" };
    return { strength: 100, text: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Reset Password Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <div className="w-full max-w-md space-y-8">
          {/* Logo/Brand */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-full h-16 bg-gradient-to-tr from-blue-900 to-gray-900 rounded-2xl flex items-center justify-center shadow-lg">
                <img src={rsbgLogo} alt="Smartweld" className="w-full h-10" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Reset Password</h2>
            <p className="mt-2 text-sm text-gray-600">
              Create a new secure password for your account
            </p>
          </div>

          {/* Reset Password Form */}
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              {/* New Password Input */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    className="appearance-none relative block w-full px-3 py-3 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                    disabled={isLoading}
                    tabIndex={-1}
                  >
                    <span className="material-symbols-outlined text-xl">
                      {showNewPassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Password strength:</span>
                      <span className={`text-xs font-medium ${
                        passwordStrength.text === "Strong" ? "text-green-600" :
                        passwordStrength.text === "Medium" ? "text-yellow-600" : "text-red-600"
                      }`}>
                        {passwordStrength.text}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Input */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    className="appearance-none relative block w-full px-3 py-3 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                    disabled={isLoading}
                    tabIndex={-1}
                  >
                    <span className="material-symbols-outlined text-xl">
                      {showConfirmPassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
                
                {/* Password Match Indicator */}
                {confirmPassword && (
                  <div className="mt-2">
                    {newPassword === confirmPassword ? (
                      <div className="flex items-center text-xs text-green-600">
                        <span className="material-symbols-outlined text-sm mr-1">check_circle</span>
                        Passwords match
                      </div>
                    ) : (
                      <div className="flex items-center text-xs text-red-600">
                        <span className="material-symbols-outlined text-sm mr-1">cancel</span>
                        Passwords do not match
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs font-medium text-blue-900 mb-2">Password Requirements:</p>
              <ul className="space-y-1">
                <li className={`text-xs flex items-center ${newPassword.length >= 8 ? "text-green-600" : "text-gray-600"}`}>
                  <span className="material-symbols-outlined text-sm mr-1">
                    {newPassword.length >= 8 ? "check_circle" : "circle"}
                  </span>
                  At least 8 characters
                </li>
                <li className={`text-xs flex items-center ${/[A-Z]/.test(newPassword) ? "text-green-600" : "text-gray-600"}`}>
                  <span className="material-symbols-outlined text-sm mr-1">
                    {/[A-Z]/.test(newPassword) ? "check_circle" : "circle"}
                  </span>
                  One uppercase letter
                </li>
                <li className={`text-xs flex items-center ${/[a-z]/.test(newPassword) ? "text-green-600" : "text-gray-600"}`}>
                  <span className="material-symbols-outlined text-sm mr-1">
                    {/[a-z]/.test(newPassword) ? "check_circle" : "circle"}
                  </span>
                  One lowercase letter
                </li>
                <li className={`text-xs flex items-center ${/[0-9]/.test(newPassword) ? "text-green-600" : "text-gray-600"}`}>
                  <span className="material-symbols-outlined text-sm mr-1">
                    {/[0-9]/.test(newPassword) ? "check_circle" : "circle"}
                  </span>
                  One number
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting password...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-xl">lock_reset</span>
                  Reset Password
                </span>
              )}
            </button>

            {/* Back to Login Link */}
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors inline-flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-sm">arrow_back</span>
                Back to Login
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Security Information */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-12 text-white">
          <div className="max-w-md">
            {/* Shield Icon */}
            <div className="mb-8">
              <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <span className="material-symbols-outlined text-5xl text-blue-300">
                  shield
                </span>
              </div>
            </div>

            {/* Security Tips */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold">Secure Your Account</h3>
              <p className="text-blue-200 text-sm leading-relaxed">
                Creating a strong password is essential to protect your account and data. Follow our guidelines to ensure maximum security.
              </p>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500/30 rounded-lg flex items-center justify-center mt-0.5">
                    <span className="material-symbols-outlined text-lg text-blue-300">check</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Use a strong password</h4>
                    <p className="text-xs text-blue-200 mt-1">Combine uppercase, lowercase, numbers, and symbols</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500/30 rounded-lg flex items-center justify-center mt-0.5">
                    <span className="material-symbols-outlined text-lg text-blue-300">block</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Avoid common patterns</h4>
                    <p className="text-xs text-blue-200 mt-1">Don't use easily guessable information like birthdays</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500/30 rounded-lg flex items-center justify-center mt-0.5">
                    <span className="material-symbols-outlined text-lg text-blue-300">vpn_key</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Keep it unique</h4>
                    <p className="text-xs text-blue-200 mt-1">Use different passwords for different accounts</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Badge */}
            <div className="mt-12 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
              <div className="flex items-center space-x-4">
                <span className="material-symbols-outlined text-4xl text-green-400">verified_user</span>
                <div>
                  <div className="text-sm font-semibold">256-bit Encryption</div>
                  <div className="text-xs text-blue-200 mt-1">Your password is encrypted using industry-standard security</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
};

export default ResetPassword;

